/**
 * CaregiverTable — one row per caregiver, expandable to that caregiver's
 * reason-code breakdown.
 *
 * The breakdown rows are rendered **into the same table** rather than as a nested
 * one. That is the whole readability difference: a nested table sets its own
 * column widths, so its Events and Avg Service figures land nowhere near the
 * columns they belong to and the block reads as an unrelated island. As sibling
 * `<tr>`s they inherit the parent's column rails, so a reason's event count sits
 * directly under the caregiver's event count and the two can be compared by eye.
 *
 * Three rendering rules that carry meaning, not just style:
 *
 *  1. **A null average renders as an em dash, never 0.0.** The backend sends null
 *     when no event qualified for a measurement. "0.0 min" asserts the work took
 *     no time; the dash says it could not be measured. Printing 0.0 would be a
 *     factual error, not a cosmetic one.
 *  2. **Average service time carries the number of events behind it.** An average
 *     over 2 of 300 events looks exactly as authoritative as one over 300 unless
 *     the count is on screen.
 *  3. **Reason rows show a share bar.** The bar is scaled to the caregiver's own
 *     busiest reason, not to the campus, because the question being asked of an
 *     expanded row is "what does this person spend their time on".
 */

import { useCallback, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';
import { sortRows } from '../../../../utils/sortRows.js';
import SortableTh from '../../../../components/v2/SortableTh.jsx';

const DASH = '—';

/** Minutes to one decimal, or a dash when the value could not be measured. */
const mins = (value) => (
    value === null || value === undefined || value === '' ? DASH : `${Number(value).toFixed(1)}`
);

/**
 * Last login in CAMPUS-local time.
 *
 * Both sources are UTC — `core_user.last_login_time` on cloud campuses and
 * DynamoDB `LastLoginTime` on non-cloud ones — and the backend serialises them
 * as `YYYY-MM-DD HH:MM:SS` with no offset. `new Date(...)` on that reads it as
 * *browser* local, so the time was wrong by the campus offset in both
 * directions: a 2am sign-in could read as the previous evening.
 *
 * convertIsoUTCToLocal is the same helper Call Logs, Maintenance and Alert
 * Details use for their timestamps, so this column now agrees with theirs.
 */
const loginLabel = (raw, tzOffset) => {
    if (!raw) return null;
    // ?? 0 because convertIsoUTCToLocal branches on `offset <= 0` / `>= 0`, and
    // undefined satisfies neither -- it would return '' and drop us to the raw
    // UTC string. 0 matches the server's payload.get("tz_offset", 0) default.
    return convertIsoUTCToLocal(raw, tzOffset ?? 0) || String(raw);
};

/**
 * Whole days since a timestamp, or null. Drives the staleness tint only.
 *
 * Parsed as UTC explicitly. This is an elapsed-time question, so the campus
 * offset is irrelevant — but the naive `new Date(raw)` was reading a UTC
 * instant as local, which shifts the answer by the offset and can flip the
 * 30-day tint on or off for a sign-in sitting near that boundary.
 */
const daysSince = (raw) => {
    if (!raw) return null;
    const d = DateTime.fromSQL(String(raw), { zone: 'utc' });
    if (!d.isValid) return null;
    return Math.floor((Date.now() - d.toMillis()) / 86400000);
};

const CaregiverTable = ({ records, totals, sortColumn, sortOrder, onSort, tzOffset }) => {
    const [expanded, setExpanded] = useState(() => new Set());

    const toggle = useCallback((userId) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(userId)) next.delete(userId);
            else next.add(userId);
            return next;
        });
    }, []);

    /* Nulls sort last in BOTH directions — a caregiver with no measurable service
       time is absent, not extreme. This table was the original of that rule; it now
       lives in utils/sortRows.js for every report table. */
    const sorted = useMemo(
        () => (sortColumn ? sortRows(records || [], sortColumn, sortOrder) : [...(records || [])]),
        [records, sortColumn, sortOrder],
    );

    /* A button inside the header, so it is keyboard-reachable; the <th> itself was the
       click target (B24). aria-sort is SortableTh's. */
    const th = (col, label, title, extraClass = '') => (
        <SortableTh
            sortKey={col}
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            onSort={onSort}
            className={`dp-th cgr-th--sortable ${extraClass}`.trim()}
            title={title}
        >
            <span className="cgr-th__inner">
                {label}
                <span className={`cgr-sort-icon${sortColumn === col ? ' is-active' : ''}`}>
                    {sortColumn === col ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}
                </span>
            </span>
        </SortableTh>
    );

    const totalRow = (totals || [])[0];
    const COLS = 8;

    return (
        <div className="dp-table-wrap cgr-table-wrap" style={{paddingLeft: 0, paddingRight: 0}}>
            <table className="dp-table cgr-table">
                <thead className="dp-thead">
                    <tr>
                        <th className="dp-th cgr-th--toggle" scope="col"><span className="cgr-sr-only">Expand</span></th>
                        {th('display_name', 'Caregiver')}
                        {th('last_login_time', 'Last Login', 'Most recent sign-in — a caregiver who has not signed in cannot receive alerts', 'cgr-th--login')}
                        {th('total_events', 'Events', 'Acknowledged events attributed to this caregiver', 'cgr-th--num')}
                        {th('avg_response_time', 'Avg Response', 'Average minutes from event start to device cleared', 'cgr-th--num')}
                        {th('avg_service_time', 'Avg Service', 'Average minutes from device cleared to event finished, over measurable events only', 'cgr-th--num')}
                        {th('no_reason_events', 'No Reason', 'Events finished without a reason code, including auto-closed events', 'cgr-th--num')}
                        {th('cleared_before_accepted', 'Cleared Before Accept', 'Events where the device was cleared before anyone accepted in the portal or app', 'cgr-th--num')}
                    </tr>
                </thead>
                <tbody>
                    {sorted.length === 0 ? (
                        <tr className="dp-tr">
                            <td className="dp-td" colSpan={COLS}>
                                <div className="na-empty">
                                    <div className="na-empty__text">No caregivers found for this campus and range</div>
                                </div>
                            </td>
                        </tr>
                    ) : null}

                    {sorted.map((row) => {
                        const isOpen = expanded.has(row.user_id);
                        const reasons = row.reasons || [];
                        const hasReasons = reasons.length > 0;
                        const silent = row.total_events === 0;
                        const hasEvents = !silent;
                        const login = loginLabel(row.last_login_time, tzOffset);
                        const age = daysSince(row.last_login_time);
                        /* Scaled to this caregiver's own busiest reason so the bars use
                           the full width of the cell whatever their absolute volume. */
                        const peak = reasons.reduce((m, r) => Math.max(m, r.total_events || 0), 0) || 1;

                        const out = [
                            <tr
                                key={row.user_id}
                                className={`dp-tr cgr-tr${silent ? ' cgr-tr--silent' : ''}${isOpen ? ' cgr-tr--open' : ''}`}
                            >
                                {/* An inline SVG, not the `▸` glyph this used to be.
                                    That character sat at 11px — too small to hit
                                    comfortably — and font coverage for U+25B8 is patchy,
                                    so where it was missing the row rendered no chevron at
                                    all rather than a fallback box. An SVG is the same size
                                    in every font. */}
                                <td className="dp-td cgr-td--toggle">
                                    {hasReasons ? (
                                        <button
                                            type="button"
                                            className="cgr-toggle"
                                            onClick={() => toggle(row.user_id)}
                                            aria-expanded={isOpen}
                                            aria-label={`${isOpen ? 'Hide' : 'Show'} reason breakdown for ${row.display_name}`}
                                        >
                                            <svg
                                                className={`cgr-toggle__chevron${isOpen ? ' is-open' : ''}`}
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth={2.5}
                                                aria-hidden="true"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    ) : hasEvents ? (
                                        /* Events, but nothing to expand: every one of them
                                           finished without a reason code, and result set 2
                                           excludes those.

                                           A DOT, not a dimmed chevron. This was a faded
                                           chevron first and that was worse than an empty
                                           cell — it still reads as a control, so it gets
                                           clicked, and doing nothing on click looks like a
                                           bug rather than an answer. A dot cannot be
                                           mistaken for a button, still fills the column so
                                           it does not look like a failed render, and says
                                           why on hover. */
                                        <span
                                            className="cgr-nochild"
                                            title="No reason codes recorded for this caregiver, so there is nothing to break down"
                                        >
                                            <span className="cgr-nochild__dot" aria-hidden="true" />
                                            <span className="cgr-sr-only">No reason breakdown</span>
                                        </span>
                                    ) : null}
                                </td>
                                <td className="dp-td cgr-td--name">
                                    <span className="cgr-name">{row.display_name}</span>
                                    {silent ? (
                                        /* Stated in words. A row of zeros with no label reads
                                           as missing data rather than as "no events". */
                                        <span className="cgr-chip cgr-chip--silent">No events</span>
                                    ) : null}
                                </td>
                                <td className="dp-td cgr-td--login">
                                    {login ? (
                                        <span className={age !== null && age > 30 ? 'cgr-login cgr-login--stale' : 'cgr-login'}>
                                            {login}
                                        </span>
                                    ) : (
                                        /* A caregiver who has never signed in cannot receive
                                           alerts at all — worth naming, not blanking. */
                                        <span className="cgr-chip cgr-chip--never">Never</span>
                                    )}
                                </td>
                                <td className="dp-td cgr-td--num cgr-td--events">{row.total_events}</td>
                                <td className="dp-td cgr-td--num">{mins(row.avg_response_time)}</td>
                                {/* The measurable-event count lives on the tooltip, not in
                                    the cell. Printed inline it read as an unexplained
                                    "51/55" in a column of minutes — the reader has to
                                    stop and work out it is a denominator, which is not
                                    what this column is for. Still reachable on hover
                                    because the ratio does matter when it is lopsided. */}
                                <td
                                    className="dp-td cgr-td--num"
                                    title={row.avg_service_time !== null && row.avg_service_time !== undefined
                                        && row.service_time_events !== row.total_events
                                        ? `Averaged over ${row.service_time_events} of ${row.total_events} events; the rest had no measurable service period`
                                        : undefined}
                                >
                                    {mins(row.avg_service_time)}
                                </td>
                                <td className="dp-td cgr-td--num">{row.no_reason_events}</td>
                                <td className="dp-td cgr-td--num">{row.cleared_before_accepted}</td>
                            </tr>,
                        ];

                        if (isOpen) {
                            out.push(
                                <tr key={`${row.user_id}-head`} className="dp-tr cgr-tr--reason cgr-tr--reason-head">
                                    <td className="dp-td" />
                                    <td className="dp-td cgr-reason-head" colSpan={2}>
                                        Reason codes
                                    </td>
                                    <td className="dp-td cgr-td--num cgr-reason-head">Events</td>
                                    <td className="dp-td" />
                                    <td className="dp-td cgr-td--num cgr-reason-head">Avg Service</td>
                                    <td className="dp-td" />
                                    <td className="dp-td" />
                                </tr>,
                            );
                            reasons.forEach((rs, i) => {
                                const last = i === reasons.length - 1;
                                out.push(
                                    <tr
                                        key={`${row.user_id}-r${rs.reason_id}`}
                                        className={`dp-tr cgr-tr--reason${last ? ' cgr-tr--reason-last' : ''}`}
                                    >
                                        <td className="dp-td" />
                                        <td className="dp-td cgr-td--reason-name" colSpan={2}>
                                            <span className="cgr-reason-label">{rs.reason_code_description}</span>
                                        </td>
                                        <td className="dp-td cgr-td--num">
                                            <span className="cgr-bar-cell">
                                                <span
                                                    className="cgr-bar"
                                                    style={{ width: `${Math.max(4, (rs.total_events / peak) * 100)}%` }}
                                                    aria-hidden="true"
                                                />
                                                <span className="cgr-bar-cell__value">{rs.total_events}</span>
                                            </span>
                                        </td>
                                        <td className="dp-td" />
                                        {/* Same treatment as the caregiver row above — the
                                            denominator is a tooltip, not a visible suffix. */}
                                        <td
                                            className="dp-td cgr-td--num"
                                            title={rs.avg_service_time !== null && rs.avg_service_time !== undefined
                                                && rs.service_time_events !== rs.total_events
                                                ? `Averaged over ${rs.service_time_events} of ${rs.total_events} events; the rest had no measurable service period`
                                                : undefined}
                                        >
                                            {mins(rs.avg_service_time)}
                                        </td>
                                        <td className="dp-td" />
                                        <td className="dp-td" />
                                    </tr>,
                                );
                            });
                        }

                        return out;
                    })}
                </tbody>

                {totalRow ? (
                    <tfoot>
                        {/* Campus roll-up over the caregivers LISTED above, not over the
                            campus's raw event count. Deprecated caregivers are excluded
                            from the roster, so a raw campus total would exceed the sum of
                            these rows and the footer would contradict the table. */}
                        <tr className="cgr-tr--totals">
                            <td className="dp-td" />
                            <td className="dp-td cgr-td--name">{totalRow.display_name || 'Totals'}</td>
                            <td className="dp-td" />
                            <td className="dp-td cgr-td--num">{totalRow.total_events}</td>
                            <td className="dp-td cgr-td--num">{mins(totalRow.avg_response_time)}</td>
                            <td className="dp-td cgr-td--num">{mins(totalRow.avg_service_time)}</td>
                            <td className="dp-td cgr-td--num">{totalRow.no_reason_events}</td>
                            <td className="dp-td cgr-td--num">{totalRow.cleared_before_accepted}</td>
                        </tr>
                    </tfoot>
                ) : null}
            </table>
        </div>
    );
};

export default CaregiverTable;
